import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import StatusBadge from '../components/StatusBadge';
import Button from '../components/ui/Button';
import SegmentedControl from '../components/ui/SegmentedControl';
import { ErrorState } from '../components/ui/States';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../hooks/usePermission';
import {
  DEBIT_NOTE_STATUS_LABELS,
  DebitNote,
  DebitNoteDraft,
  DebitNoteEmailDraft,
  DebitNoteKind,
  DebitNoteLine,
  DebitNotePreview,
  GOODS_STATE_LABELS,
  GoodsState,
  ReturnableInvoice,
  ReturnableLine,
  approveDebitNote,
  approveDebitNoteWriteOff,
  cancelDebitNote,
  createDebitNote,
  creditedAmountOf,
  declineDebitNoteWriteOff,
  downloadDebitNotePdf,
  emailDebitNoteToVendor,
  getDebitNote,
  getDebitNoteEmailDraft,
  getNextDebitNoteNumber,
  getReturnableInvoice,
  isDebitNoteEditable,
  markDebitNoteSent,
  markVendorAccepted,
  markVendorRefused,
  previewDebitNote,
  releaseDebitNoteGoods,
  requestDebitNoteWriteOff,
  reverseDebitNote,
  sendBackDebitNote,
  updateDebitNote,
} from '../lib/debitNoteApi';
import { CreditReason, listCreditReasons } from '../lib/creditReasonApi';
import CreditReasonField from '../features/creditNotes/CreditReasonField';
import BoxPicker from '../features/debitNotes/BoxPicker';
import DebitNoteHistory from '../features/debitNotes/DebitNoteHistory';
import EmailToVendorDialog from '../features/debitNotes/EmailToVendorDialog';
import NoteDialog from '../features/debitNotes/NoteDialog';
import VendorCreditNotePanel from '../features/debitNotes/VendorCreditNotePanel';
import VendorInvoicePicker from '../features/debitNotes/VendorInvoicePicker';
import {
  GOODS_STATE_TONES,
  batchFactsOf,
  errorMessageOf,
  formatDebitNoteMoney,
  refusedLineNumbers,
} from '../features/debitNotes/debitNoteDisplay';
import { formatDate } from '../utils/dateFormat';

/**
 * A vendor return (debit note, DN-00001), D44 (2026-09-17): United telling a
 * supplier it is sending goods back, or claiming money off ONE of its invoices.
 * Lives in the Credit Notes module, Vendor tab.
 *
 * The server works out every figure. The form holds only what was ticked and
 * typed, asks `previewDebitNote` for the amounts, VAT and totals, and shows what
 * comes back, so the screen and the saved return cannot disagree. The caps (what
 * a line billed, less what other returns already claim) are the server's too;
 * a refusal names its line, and that row is marked.
 *
 * Goods go back from the batch (StockLayer) their invoice line was received into
 * (VR-3): only what is free in it, and box by box when it is weighed catch
 * weight. Marking a goods return as sent HOLDS the goods on that batch; what
 * happens to them next (released, returned, written off, restored) is
 * `goods.state`, moved only by the server.
 */

const BACK_TO = '/credit-notes?tab=vendor';

const PAGE_INFO = (
  <div className="flex flex-col gap-3">
    <p>
      A vendor return tells a supplier that goods are going back, or claims money off one of their invoices. It is
      written against one confirmed vendor invoice, in that invoice&apos;s currency.
    </p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>
        Goods going back: say how many of each line go back, or tick the boxes when the goods were weighed box by box.
        The amount is suggested from the invoice and can be changed.
      </li>
      <li>Money only: a price mistake, a rebate, or goods not returned. Type an amount excluding VAT per line.</li>
    </ul>
    <p>
      Each line is capped at what it billed, less what other returns already claim on it. Goods can only go back from
      what is free in the batch the line was received into: not what orders reserved or other returns hold.
    </p>
    <p>
      A return is a Draft until it is sent: Email to vendor sends the vendor&apos;s copy (a PDF) and makes it Sent once
      the email has gone; Mark as sent records a return sent some other way. Sending a goods return holds its goods: still counted in stock, but
      no order can take them. When the vendor accepts it, an Admin approves it, and only then is the bill credited and
      the goods taken out of stock. If the vendor refuses, the held goods are released, or written off with an
      Admin&apos;s approval. The reason and its note are printed on the vendor&apos;s copy.
    </p>
  </div>
);

const KIND_OPTIONS: Array<{ value: DebitNoteKind; label: string }> = [
  { value: 'goods', label: 'Goods going back' },
  { value: 'money_only', label: 'Money only' },
];

// Goods only (VR-3): the server refuses these with DN_BATCH_NOT_FOUND and
// DN_BATCH_UNIT_UNKNOWN; the form says so before anything is ticked.
const NO_BATCH = 'This line put no goods into United stock, so only a money-only return can credit it';
const BATCH_UNIT_UNKNOWN =
  "This line's stock receipt does not say how many it received, so only a money-only return can credit it";
const BATCH_NOTHING_FREE = 'Nothing in its batch is free to go back';

/** What the form holds for one invoice line: ticked or not, and the boxes exactly as typed. */
type LineEntry = {
  included: boolean;
  /** Goods only. */
  quantity: string;
  /**
   * Goods on a batch of weighed boxes: the box seqs ticked, sorted. They are the
   * quantity, so none is typed. null when the line goes back by quantity.
   */
  caseSeqs: number[] | null;
  /** Goods: null leaves the amount to the server's suggestion. Money only: the amount claimed. */
  amount: string | null;
  /** The amount was read back from the saved return rather than typed here. */
  amountSaved: boolean;
};

type Entries = Record<number, LineEntry>;

const UNTOUCHED: LineEntry = { included: false, quantity: '', caseSeqs: null, amount: null, amountSaved: false };

type NoteAction = 'refused' | 'send_back' | 'reverse' | 'release_goods' | 'request_write_off' | 'decline_write_off';

/** The move each note-carrying action makes, and what to say when it is refused without a message. */
const NOTE_ACTIONS: Record<NoteAction, { fallback: string; call: (id: string, text: string) => Promise<DebitNote> }> = {
  refused: {
    fallback: 'The vendor return could not be marked as refused.',
    call: (id, text) => markVendorRefused(id, text),
  },
  send_back: { fallback: 'The vendor return could not be sent back.', call: (id, text) => sendBackDebitNote(id, text) },
  reverse: { fallback: 'The vendor return could not be reversed.', call: (id, text) => reverseDebitNote(id, text) },
  release_goods: {
    fallback: 'The held goods could not be released.',
    call: (id, text) => releaseDebitNoteGoods(id, text),
  },
  request_write_off: {
    fallback: 'The write-off could not be requested.',
    call: (id, text) => requestDebitNoteWriteOff(id, text),
  },
  decline_write_off: {
    fallback: 'The write-off could not be declined.',
    call: (id, text) => declineDebitNoteWriteOff(id, text),
  },
};

/** The lines a preview or a save sends, in line order. */
function draftLinesOf(kind: DebitNoteKind, entries: Entries): DebitNoteDraft['lines'] {
  return Object.keys(entries)
    .map(Number)
    .filter((lineNumber) => entries[lineNumber].included)
    .sort((a, b) => a - b)
    .map((lineNumber) => {
      const entry = entries[lineNumber];
      // An empty box goes as no number, never as 0: the server parses what is
      // typed and names the line when something is missing or wrong.
      const amountText = (entry.amount ?? '').trim();
      const amount = amountText === '' ? null : Number(amountText);
      if (kind === 'money_only') return { lineNumber, amount };
      const quantityText = entry.quantity.trim();
      // Weighed boxes go as the boxes, never as a quantity: the server weighs them.
      const line: DebitNoteDraft['lines'][number] = entry.caseSeqs
        ? { lineNumber, caseSeqs: [...entry.caseSeqs].sort((a, b) => a - b) }
        : { lineNumber, quantity: quantityText === '' ? null : Number(quantityText) };
      // Goods: an amount only when one was typed. Without it the server prices the
      // quantity at the line's share of what it billed.
      if (amount !== null) line.amount = amount;
      return line;
    });
}

/** A ticked line still missing the one figure its kind needs, so there is nothing to preview yet. */
function hasIncompleteLine(kind: DebitNoteKind, entries: Entries): boolean {
  return Object.values(entries).some((entry) => {
    if (!entry.included) return false;
    if (kind === 'money_only') return (entry.amount ?? '').trim() === '';
    return entry.caseSeqs ? entry.caseSeqs.length === 0 : entry.quantity.trim() === '';
  });
}

/** Everything a save would send, as one comparable string: how "unsaved changes" is told. */
function draftKeyOf(kind: DebitNoteKind, reasonKey: string, reasonNote: string, entries: Entries): string {
  return JSON.stringify({ kind, reasonKey, reasonNote, lines: draftLinesOf(kind, entries) });
}

function entriesFromNote(note: DebitNote): Entries {
  const entries: Entries = {};
  (note.lines || []).forEach((line) => {
    entries[line.lineNumber] = {
      included: true,
      quantity: typeof line.quantity === 'number' ? String(line.quantity) : '',
      caseSeqs: note.kind === 'goods' && line.caseSeqs?.length ? [...line.caseSeqs].sort((a, b) => a - b) : null,
      // Kept as typed, so re-saving a return whose goods amount was edited keeps the edit.
      amount: String(line.amount),
      amountSaved: note.kind === 'goods',
    };
  });
  return entries;
}

/** The figures a saved return stores, in the preview's shape. */
function figuresOf(note: DebitNote): DebitNotePreview {
  return {
    currency: note.currency,
    kind: note.kind,
    lines: note.lines || [],
    subtotal: note.subtotal,
    vatTotal: note.vatTotal,
    total: note.total,
  };
}

/** A saved line in the table's shape, for when the invoice's own lines cannot be read. */
function tableLineFromSaved(line: DebitNoteLine): ReturnableLine {
  return {
    lineNumber: line.lineNumber,
    description: line.description || '',
    sku: line.sku || '',
    unit: line.unit || '',
    wholeUnitsOnly: false,
    quantity: null,
    unitPrice: line.unitPrice ?? null,
    base: null,
    taxRate: line.taxRate ?? null,
    taxRateAssumed: line.taxRateAssumed === true,
    returnedQuantity: 0,
    returnedAmount: 0,
    returnedOn: [],
    remainingQuantity: null,
    remainingAmount: null,
    // Unknown here, not "no batch": nothing is blocked on a line read back.
    batch: null,
  };
}

/**
 * Why a line cannot be ticked. Only the invoice's own lines say: a saved line read
 * back has no caps. `goodsBatch` adds the batch's word on a goods draft (VR-3).
 */
function blockedReason(line: ReturnableLine, fromInvoice: boolean, goodsBatch: boolean): string | null {
  if (!fromInvoice) return null;
  if (line.remainingAmount === null) return 'No quantity and unit price on the invoice line to credit';
  if (line.remainingAmount === 0) return 'Nothing left to return';
  if (goodsBatch) {
    const batch = line.batch;
    if (!batch) return NO_BATCH;
    if (!batch.returnableByQuantity) return BATCH_UNIT_UNKNOWN;
    const nothingFree = batch.boxes ? !batch.boxes.some((box) => box.available) : batch.freeQuantity === 0;
    if (nothingFree) return BATCH_NOTHING_FREE;
  }
  return null;
}

/** Where a goods return's goods are. null on money only, and before Sent. */
function goodsStateOf(note: DebitNote | null): GoodsState {
  return note?.kind === 'goods' ? note.goods?.state ?? null : null;
}

function quantityWithUnit(quantity: number | null, unit: string): string {
  return quantity === null ? '--' : `${quantity} ${unit}`.trim();
}

export const DebitNoteDetailPage: React.FC = () => {
  const params = useParams();
  const id = params.id;
  const isNew = !id || id === 'new';
  const [searchParams] = useSearchParams();
  const presetInvoiceId = searchParams.get('vendorInvoiceId') || '';
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();
  // The Admin ROLE, not a permission: approving, sending back and reversing are
  // the Admin's alone (D44; debitNote.routes.js `isAdmin`).
  const isAdmin = user?.role === 'admin';
  const { canCreate, canEdit } = usePermission('invoices');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [dialog, setDialog] = useState<NoteAction | null>(null);
  // "Email to vendor" (VR-6): what the dialog opens with, once the draft is saved.
  const [emailDialog, setEmailDialog] = useState<DebitNoteEmailDraft | null>(null);
  // A message that must survive the reload a navigation triggers (see handleMarkSent).
  const carriedError = useRef<string | null>(null);

  /** The saved return; null until the first save. */
  const [note, setNote] = useState<DebitNote | null>(null);
  const [nextNumber, setNextNumber] = useState('');
  // D44: loaded WITH hidden reasons, so a return filed under one still shows it.
  const [reasons, setReasons] = useState<CreditReason[]>([]);

  const [vendorInvoiceId, setVendorInvoiceId] = useState('');
  const [returnableReload, setReturnableReload] = useState(0);
  const [returnable, setReturnable] = useState<ReturnableInvoice | null>(null);
  const [returnableLoading, setReturnableLoading] = useState(false);
  const [returnableError, setReturnableError] = useState<string | null>(null);

  const [kind, setKind] = useState<DebitNoteKind>('goods');
  const [reasonKey, setReasonKey] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [entries, setEntries] = useState<Entries>({});
  const [savedKey, setSavedKey] = useState('');

  const [preview, setPreview] = useState<DebitNotePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** Lines the last refusal named: marked in the table. */
  const [flaggedLines, setFlaggedLines] = useState<number[]>([]);

  const editable = !note || isDebitNoteEditable(note.status);
  const noteId = note?._id;

  /** Replace the page with a return as the server now has it. */
  const applyNote = (next: DebitNote) => {
    const seeded = entriesFromNote(next);
    setNote(next);
    setVendorInvoiceId(String(next.vendorInvoiceId || ''));
    setKind(next.kind);
    setReasonKey(next.reasonKey || '');
    setReasonNote(next.reasonNote || '');
    setEntries(seeded);
    setSavedKey(draftKeyOf(next.kind, next.reasonKey || '', next.reasonNote || '', seeded));
    setPreview(figuresOf(next));
    setPreviewError(null);
    setFlaggedLines([]);
  };

  useEffect(() => {
    let cancelled = false;
    listCreditReasons({ includeHidden: true })
      .then((list) => {
        if (!cancelled) setReasons(list);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorMessageOf(e, 'Could not load the reason list'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the return, or start a new one (on the invoice the link names, if any).
  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        if (isNew) {
          setNote(null);
          setVendorInvoiceId(presetInvoiceId);
          setKind('goods');
          setReasonKey('');
          setReasonNote('');
          setEntries({});
          setSavedKey('');
          setPreview(null);
          const number = await getNextDebitNoteNumber();
          if (!cancelled) setNextNumber(number);
        } else if (id) {
          const loaded = await getDebitNote(id);
          if (!cancelled) applyNote(loaded);
        }
      } catch (e) {
        if (!cancelled) {
          setError(errorMessageOf(e, isNew ? 'The next DN number could not be read.' : 'The vendor return could not be loaded.'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          if (carriedError.current) {
            setError(carriedError.current);
            carriedError.current = null;
          }
        }
      }
    }
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The invoice's lines and what is left to return on each. A draft's own lines
  // hold part of the allowance already, so they are not counted against it; a
  // return past Draft is shown against what is left after it.
  useEffect(() => {
    if (!vendorInvoiceId) {
      setReturnable(null);
      setReturnableError(null);
      setReturnableLoading(false);
      return;
    }
    let cancelled = false;
    setReturnableLoading(true);
    setReturnableError(null);
    getReturnableInvoice(vendorInvoiceId, editable && noteId ? noteId : undefined)
      .then((data) => {
        if (!cancelled) setReturnable(data);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setReturnable(null);
        // e.g. DN_INVOICE_NOT_CONFIRMED: the server's own words say what to do instead.
        setReturnableError(errorMessageOf(e, "This invoice's lines could not be loaded."));
      })
      .finally(() => {
        if (!cancelled) setReturnableLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vendorInvoiceId, noteId, editable, returnableReload]);

  const draftLines = useMemo(() => draftLinesOf(kind, entries), [kind, entries]);
  const incomplete = useMemo(() => hasIncompleteLine(kind, entries), [kind, entries]);
  const linesKey = JSON.stringify(draftLines);
  const dirty = !note || draftKeyOf(kind, reasonKey, reasonNote, entries) !== savedKey;

  // The figures, from the server, whenever what is ticked or typed changes.
  useEffect(() => {
    if (!editable || !vendorInvoiceId) return;
    if (!draftLines.length || incomplete) {
      setPreview(null);
      setPreviewError(null);
      setFlaggedLines([]);
      setPreviewing(false);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    const t = window.setTimeout(async () => {
      try {
        const result = await previewDebitNote({
          vendorInvoiceId,
          kind,
          reasonKey,
          reasonNote,
          lines: draftLines,
          ...(noteId ? { id: noteId } : {}),
        });
        if (cancelled) return;
        setPreview(result);
        setPreviewError(null);
        setFlaggedLines([]);
      } catch (e) {
        if (cancelled) return;
        setPreview(null);
        setPreviewError(errorMessageOf(e, 'The totals could not be worked out. Try again.'));
        setFlaggedLines(refusedLineNumbers(e));
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // The reason rides along but changes no figure, so typing it does not re-ask.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, vendorInvoiceId, kind, linesKey, incomplete, noteId]);

  const patchEntry = (lineNumber: number, change: (prev: LineEntry) => LineEntry) =>
    setEntries((prev) => ({ ...prev, [lineNumber]: change(prev[lineNumber] ?? UNTOUCHED) }));

  const toggleLine = (line: ReturnableLine, included: boolean) =>
    patchEntry(line.lineNumber, (e) => ({
      ...e,
      included,
      // A batch of weighed boxes goes back by its boxes from the first tick.
      caseSeqs: line.batch?.boxes ? e.caseSeqs ?? [] : e.caseSeqs,
    }));

  const toggleBox = (lineNumber: number, seq: number, ticked: boolean) =>
    patchEntry(lineNumber, (e) => {
      const others = (e.caseSeqs ?? []).filter((s) => s !== seq);
      return {
        ...e,
        caseSeqs: (ticked ? [...others, seq] : others).sort((a, b) => a - b),
        // As with a typed quantity: a saved amount belonged to the old boxes.
        ...(e.amountSaved ? { amount: null, amountSaved: false } : {}),
      };
    });

  const setQuantity = (line: ReturnableLine, text: string) => {
    // A counted unit (CASE, BTL) goes back whole, so a part never gets into the box.
    if (line.wholeUnitsOnly && text.trim() !== '' && !/^\d+$/.test(text.trim())) return;
    patchEntry(line.lineNumber, (e) => ({
      ...e,
      quantity: text,
      // An amount read back from the saved return belonged to the old quantity:
      // hand it back to the server's suggestion.
      ...(e.amountSaved ? { amount: null, amountSaved: false } : {}),
    }));
  };

  const setAmount = (lineNumber: number, text: string) =>
    patchEntry(lineNumber, (e) => ({ ...e, amount: text, amountSaved: false }));

  /** Goods: an amount box left empty goes back to the server's suggestion. Held as '' while typing. */
  const releaseAmount = (lineNumber: number) =>
    patchEntry(lineNumber, (e) => ((e.amount ?? '').trim() === '' ? { ...e, amount: null } : e));

  const changeKind = (next: DebitNoteKind) => {
    if (next === kind) return;
    setKind(next);
    // Ticks and quantities stay; amounts do not. A goods amount is a price for a
    // quantity, a money-only amount is the claim itself, and carrying one over as
    // the other would claim a figure nobody typed for it.
    setEntries((prev) => {
      const out: Entries = {};
      Object.keys(prev).forEach((key) => {
        out[Number(key)] = { ...prev[Number(key)], amount: null, amountSaved: false };
      });
      return out;
    });
  };

  const pickInvoice = (nextId: string) => {
    if (nextId && nextId === vendorInvoiceId) setReturnableReload((n) => n + 1);
    setVendorInvoiceId(nextId);
    setEntries({});
    setPreview(null);
    setPreviewError(null);
    setFlaggedLines([]);
    setError(null);
  };

  const showActionError = (e: unknown, fallback: string) => {
    setError(errorMessageOf(e, fallback));
    setFlaggedLines(refusedLineNumbers(e));
  };

  /** What stops a save before it is sent; the server refuses the same things with codes. */
  const saveBlocker = (): string | null => {
    if (!vendorInvoiceId || (!note && !returnable)) return 'Choose the vendor invoice this return is for.';
    if (!draftLines.length) return 'Tick at least one invoice line to return.';
    if (!reasonKey) return 'Choose a reason for this return.';
    const reason = reasons.find((r) => r.key === reasonKey);
    if (reason?.noteRequired && !reasonNote.trim()) return `Add a note to explain "${reason.label}".`;
    return null;
  };

  /** Create or update the draft; resolves to the saved return. */
  const persistDraft = (): Promise<DebitNote> => {
    if (!note) return createDebitNote({ vendorInvoiceId, kind, reasonKey, reasonNote, lines: draftLines });
    // No invoice id: a return stays on the invoice it was raised against.
    return updateDebitNote(note._id, { kind, reasonKey, reasonNote, lines: draftLines });
  };

  const handleSave = async () => {
    const blocker = saveBlocker();
    if (blocker) {
      setError(blocker);
      return;
    }
    setBusy('save');
    setError(null);
    try {
      const saved = await persistDraft();
      applyNote(saved);
      if (!note) navigate(`/credit-notes/vendor/${saved._id}`);
    } catch (e) {
      showActionError(e, 'The vendor return could not be saved.');
    } finally {
      setBusy(null);
    }
  };

  // Mark as sent saves the form first when it has changed, so what goes to the
  // vendor is what is on screen, never an older copy of the draft.
  const handleMarkSent = async () => {
    const blocker = dirty ? saveBlocker() : null;
    if (blocker) {
      setError(blocker);
      return;
    }
    const ok = await confirm({
      title: `Mark ${note ? note.debitNoteNumber : 'this return'} as sent?`,
      summary:
        kind === 'goods'
          ? 'Records that the return has gone to the vendor, and holds its goods in their batch: still counted in stock, but no order can take them. It can no longer be edited; to change it, cancel it and raise a new one.'
          : 'Records that the return has gone to the vendor. It can no longer be edited; to change it, cancel it and raise a new one.',
      confirmLabel: 'Mark as sent',
    });
    if (!ok) return;
    setBusy('mark_sent');
    setError(null);
    let target: DebitNote;
    try {
      target = !note || dirty ? await persistDraft() : note;
    } catch (e) {
      showActionError(e, 'The vendor return could not be saved.');
      setBusy(null);
      return;
    }
    try {
      applyNote(await markDebitNoteSent(target._id));
      if (!note) navigate(`/credit-notes/vendor/${target._id}`);
    } catch (e) {
      const message = errorMessageOf(e, 'The vendor return could not be marked as sent.');
      if (!note) {
        // The draft exists now. Move onto it, so trying again updates THAT draft
        // instead of creating a second one, and keep the reason it was not sent.
        carriedError.current = `Saved as a draft, but not marked as sent: ${message}`;
        applyNote(target);
        navigate(`/credit-notes/vendor/${target._id}`);
      } else {
        if (target !== note) applyNote(target);
        showActionError(e, message);
      }
    } finally {
      setBusy(null);
    }
  };

  /** One status move: the page becomes the return the server hands back. */
  const runAction = async (name: string, fallback: string, call: () => Promise<DebitNote>) => {
    setBusy(name);
    setError(null);
    try {
      applyNote(await call());
    } catch (e) {
      showActionError(e, fallback);
    } finally {
      setBusy(null);
    }
  };

  // Email to vendor saves the form first when it has changed, like Mark as sent,
  // so the PDF the vendor gets is what is on screen.
  const handleEmailOpen = async () => {
    if (!note) return;
    const blocker = dirty ? saveBlocker() : null;
    if (blocker) {
      setError(blocker);
      return;
    }
    setBusy('email_open');
    setError(null);
    try {
      const target = dirty ? await persistDraft() : note;
      if (target !== note) applyNote(target);
      setEmailDialog(await getDebitNoteEmailDraft(target._id));
    } catch (e) {
      showActionError(e, 'The vendor return could not be saved.');
    } finally {
      setBusy(null);
    }
  };

  const handleEmailSend = async (body: { to: string; message: string }) => {
    if (!note) return;
    const target = note._id;
    setBusy('email');
    setError(null);
    try {
      applyNote(await emailDebitNoteToVendor(target, body));
    } catch (e) {
      showActionError(e, 'The vendor return could not be emailed.');
    } finally {
      setEmailDialog(null);
      setBusy(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (!note) return;
    setBusy('pdf');
    setError(null);
    try {
      await downloadDebitNotePdf(note._id, note.debitNoteNumber);
    } catch (e) {
      showActionError(e, 'The PDF could not be downloaded.');
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    if (!note) return;
    const releasesGoods = note.status === 'sent' && goodsStateOf(note) === 'held';
    const ok = await confirm({
      title: `Cancel vendor return ${note.debitNoteNumber}?`,
      summary: releasesGoods
        ? "It stops counting against the invoice's lines, and its held goods go back into free stock. A cancelled return cannot be edited or sent again."
        : "It stops counting against the invoice's lines. A cancelled return cannot be edited or sent again.",
      tone: 'error',
      confirmLabel: 'Cancel return',
      cancelLabel: 'Keep it',
    });
    if (!ok) return;
    const target = note._id;
    await runAction('cancel', 'The vendor return could not be cancelled.', () => cancelDebitNote(target));
  };

  const handleVendorAccepted = async () => {
    if (!note) return;
    const ok = await confirm({
      title: `Vendor accepted ${note.debitNoteNumber}?`,
      summary:
        goodsStateOf(note) === 'held'
          ? 'This sends it to an Admin for approval. Nothing changes in Accounts until it is approved, and the goods stay held.'
          : 'This sends it to an Admin for approval. Nothing changes in Accounts until it is approved.',
      confirmLabel: 'Vendor accepted',
    });
    if (!ok) return;
    const target = note._id;
    await runAction('accepted', 'The vendor return could not be marked as accepted.', () => markVendorAccepted(target));
  };

  const handleApprove = async () => {
    if (!note) return;
    const ok = await confirm({
      title: `Approve ${note.debitNoteNumber}?`,
      summary: `The bill for invoice ${note.vendorInvoiceNumber || '--'} is credited ${formatDebitNoteMoney(
        creditedAmountOf(note),
        note.currency
      )}. Anything beyond what it still owes becomes a credit balance with the vendor.${
        goodsStateOf(note) === 'held' ? ' The held goods leave United stock.' : ''
      }`,
      confirmLabel: 'Approve',
    });
    if (!ok) return;
    const target = note._id;
    await runAction('approve', 'The vendor return could not be approved.', () => approveDebitNote(target));
  };

  const handleApproveWriteOff = async () => {
    if (!note) return;
    const ok = await confirm({
      title: `Write off the goods on ${note.debitNoteNumber}?`,
      summary:
        'The held goods are removed from United stock, and nothing is credited for them. This cannot be undone.',
      tone: 'error',
      confirmLabel: 'Approve write-off',
      cancelLabel: 'Back',
    });
    if (!ok) return;
    const target = note._id;
    await runAction('approve_write_off', 'The write-off could not be approved.', () => approveDebitNoteWriteOff(target));
  };

  const runNoteAction = async (action: NoteAction, text: string) => {
    if (!note) return;
    const target = note._id;
    const { fallback, call } = NOTE_ACTIONS[action];
    await runAction(action, fallback, () => call(target, text));
    setDialog(null);
  };

  const backButton = (
    <button
      onClick={() => navigate(BACK_TO)}
      aria-label="Back to vendor returns"
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
    >
      <ArrowLeft aria-hidden="true" className="h-4 w-4" />
    </button>
  );

  if (loading) {
    return (
      <div className="h-full">
        <LoadingState text="Loading vendor return..." />
      </div>
    );
  }

  // An existing return that could not be read: nothing below may act as if this were a new one.
  if (!isNew && !note) {
    return (
      <div className="h-full flex flex-col bg-brand-bg">
        <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
          <div className="flex items-center gap-2">
            {backButton}
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Vendor return</h1>
          </div>
        </div>
        <div className="px-4 py-4 sm:px-8">
          <ErrorState message={error || 'The vendor return could not be loaded.'} />
        </div>
      </div>
    );
  }

  const status = note?.status;
  const mayWrite = note ? canEdit : canCreate;
  const readOnly = !editable || !mayWrite;
  const busyNow = busy !== null;
  const currency = note?.currency || preview?.currency || returnable?.vendorInvoice.currency || '';
  const money = (value: number | null | undefined) => formatDebitNoteMoney(value, currency);
  const vendorName = note?.vendorName || returnable?.vendorInvoice.vendorName || '';
  const invoiceNumber = note?.vendorInvoiceNumber || returnable?.vendorInvoice.invoiceNumber || '';
  const invoiceDate = returnable?.vendorInvoice.invoiceDate;
  const fromInvoice = Boolean(returnable);
  const tableLines: ReturnableLine[] = returnable?.lines ?? (note ? (note.lines || []).map(tableLineFromSaved) : []);
  const figureByLine = new Map((preview?.lines || []).map((line) => [line.lineNumber, line]));
  const application = note?.application;
  const goodsState = goodsStateOf(note);
  // Goods figures and boxes only matter while a goods return is being written.
  const goodsDraft = editable && kind === 'goods';

  const dialogCopy = (() => {
    const number = note?.debitNoteNumber;
    switch (dialog) {
      case 'refused':
        return {
          title: `Vendor refused ${number}?`,
          summary:
            goodsState === 'held'
              ? 'The return is closed as refused. Nothing changes in Accounts. The goods stay held until they are released, or an Admin approves writing them off.'
              : 'The return is closed as refused. Nothing changes in Accounts.',
          noteLabel: 'What the vendor said (optional)',
          confirmLabel: 'Vendor refused',
          danger: true,
        };
      case 'send_back':
        return {
          title: `Send back ${number}?`,
          summary:
            goodsState === 'held'
              ? 'It goes back to Sent to vendor without being approved. Nothing changes in Accounts, and the goods stay held.'
              : 'It goes back to Sent to vendor without being approved. Nothing changes in Accounts.',
          noteLabel: 'Why (optional)',
          confirmLabel: 'Send back',
          danger: false,
        };
      case 'release_goods':
        return {
          title: `Release the goods on ${number}?`,
          summary:
            goodsState === 'write_off_requested'
              ? 'The held goods go back into free stock in their batch, where orders can take them. The write-off request is withdrawn.'
              : 'The held goods go back into free stock in their batch, where orders can take them.',
          noteLabel: 'Why (optional)',
          confirmLabel: 'Release goods',
          danger: false,
        };
      case 'request_write_off':
        return {
          title: `Request a write-off for ${number}?`,
          summary:
            'An Admin decides. Until then the goods stay held; if the write-off is approved they leave United stock for good.',
          noteLabel: 'Why the goods cannot go back into stock',
          noteRequired: true,
          confirmLabel: 'Request write-off',
          danger: false,
        };
      case 'decline_write_off':
        return {
          title: `Decline the write-off on ${number}?`,
          summary: 'The goods stay held. They can still be released, or a write-off asked for again.',
          noteLabel: 'Why (optional)',
          confirmLabel: 'Decline write-off',
          danger: false,
        };
      default:
        return {
          title: `Reverse ${number}?`,
          warning:
            goodsState === 'returned'
              ? 'The bill goes back up, and the goods go back into their batch. Refused if its credit balance was already applied to another bill.'
              : 'The bill goes back up. Refused if its credit balance was already applied to another bill.',
          noteLabel: 'Why (optional)',
          confirmLabel: 'Reverse',
          danger: true,
        };
    }
  })();

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* A detail page, so the title is the return itself and the way back sits in
          the header's left group, as on a client credit note. */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {backButton}
            <h1 className="text-xl font-semibold leading-7 text-slate-900">
              {note ? `Vendor return ${note.debitNoteNumber}` : `Vendor return${nextNumber ? ` ${nextNumber} (next)` : ''}`}
            </h1>
            {status && (
              <span data-testid="debit-note-status">
                <StatusBadge status={DEBIT_NOTE_STATUS_LABELS[status]} />
              </span>
            )}
            {goodsState && (
              <span data-testid="debit-note-goods-state">
                <StatusBadge status={GOODS_STATE_LABELS[goodsState]} tone={GOODS_STATE_TONES[goodsState]} />
              </span>
            )}
            <ModuleInfoButton content={PAGE_INFO} ariaLabel="Vendor return information" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* The vendor's copy, in any status (D44 #17). What is saved, so not while the form has changes. */}
            {note && (
              <Button
                onClick={handleDownloadPdf}
                loading={busy === 'pdf'}
                disabled={busyNow || (editable && dirty)}
                title={editable && dirty ? 'Save the draft first: the PDF is the saved return' : "The vendor's copy, as a PDF"}
              >
                Download PDF
              </Button>
            )}
            {editable && (
              <>
                {note && canEdit && (
                  <Button variant="danger" onClick={handleCancel} loading={busy === 'cancel'} disabled={busyNow}>
                    Cancel return
                  </Button>
                )}
                {mayWrite && (
                  <Button onClick={handleSave} loading={busy === 'save'} disabled={busyNow}>
                    Save draft
                  </Button>
                )}
                {mayWrite && canEdit && (
                  <Button
                    // Email to vendor is the usual way once the draft is saved; this records one sent another way.
                    variant={note ? 'secondary' : 'primary'}
                    onClick={handleMarkSent}
                    loading={busy === 'mark_sent'}
                    disabled={busyNow}
                    title="Save, and record that the return has gone to the vendor"
                  >
                    Mark as sent
                  </Button>
                )}
                {note && canEdit && (
                  <Button
                    variant="primary"
                    onClick={handleEmailOpen}
                    loading={busy === 'email_open' || busy === 'email'}
                    disabled={busyNow}
                    title="Save, email the vendor's copy, and make the return Sent once the email has gone"
                  >
                    Email to vendor
                  </Button>
                )}
              </>
            )}
            {status === 'sent' && canEdit && (
              <>
                <Button variant="danger" onClick={handleCancel} loading={busy === 'cancel'} disabled={busyNow}>
                  Cancel return
                </Button>
                <Button onClick={() => setDialog('refused')} disabled={busyNow}>
                  Vendor refused
                </Button>
                <Button variant="primary" onClick={handleVendorAccepted} loading={busy === 'accepted'} disabled={busyNow}>
                  Vendor accepted
                </Button>
              </>
            )}
            {status === 'awaiting_approval' && isAdmin && (
              <>
                <Button onClick={() => setDialog('send_back')} disabled={busyNow}>
                  Send back
                </Button>
                <Button variant="primary" onClick={handleApprove} loading={busy === 'approve'} disabled={busyNow}>
                  Approve
                </Button>
              </>
            )}
            {status === 'approved' && isAdmin && (
              <Button variant="danger" onClick={() => setDialog('reverse')} disabled={busyNow}>
                Reverse
              </Button>
            )}
            {/* Refused by the vendor: the held goods wait for a person (D44 #14, #23). */}
            {status === 'rejected' && goodsState === 'held' && canEdit && (
              <>
                <Button onClick={() => setDialog('request_write_off')} disabled={busyNow}>
                  Request write-off
                </Button>
                <Button variant="primary" onClick={() => setDialog('release_goods')} disabled={busyNow}>
                  Release goods
                </Button>
              </>
            )}
            {status === 'rejected' && goodsState === 'write_off_requested' && (
              <>
                {canEdit && (
                  <Button onClick={() => setDialog('release_goods')} disabled={busyNow}>
                    Release goods
                  </Button>
                )}
                {isAdmin && (
                  <>
                    <Button onClick={() => setDialog('decline_write_off')} disabled={busyNow}>
                      Decline write-off
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleApproveWriteOff}
                      loading={busy === 'approve_write_off'}
                      disabled={busyNow}
                    >
                      Approve write-off
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Vendor: {vendorName || '--'} • Invoice: {invoiceNumber || '--'} • Currency: {currency || '--'}
          {/* VR-4: raised by "Return to supplier?" on a client credit note. */}
          {note?.clientCreditNoteId && (
            <>
              {' '}
              • Raised from credit note{' '}
              <Link
                to={`/credit-notes/${note.clientCreditNoteId}`}
                className="font-medium text-primary-600 hover:underline"
              >
                {note.clientCreditNoteNumber || 'credit note'}
              </Link>
            </>
          )}
        </p>
      </div>

      {/* D161.9: bottom room (as the invoice pane) so the assistant bubble never covers the total. */}
      <div data-testid="debit-note-body" className="px-4 py-4 pb-24 flex-1 overflow-auto space-y-4 sm:px-8">
        {error && <ErrorState message={error} />}

        {note && !editable && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 space-y-1">
            {status === 'sent' && (
              <p>
                {note.sentVia === 'email' ? `Emailed to ${note.sentTo || 'the vendor'}` : 'Sent to the vendor'}
                {note.sentAt ? ` on ${formatDate(note.sentAt, '')}` : ''}. When they answer, record it with Vendor
                accepted or Vendor refused.
              </p>
            )}
            {goodsState === 'held' && (status === 'sent' || status === 'awaiting_approval') && (
              <p>Its goods are held in their batch: still counted in stock, but no order can take them.</p>
            )}
            {status === 'awaiting_approval' &&
              (isAdmin ? (
                <p>
                  The vendor accepted this return. Approving it credits the bill for invoice {invoiceNumber || '--'}
                  {goodsState === 'held' ? ' and takes the held goods out of United stock' : ''}.
                </p>
              ) : (
                <p>Waiting for an Admin to approve.</p>
              ))}
            {status === 'approved' && goodsState === 'returned' && <p>The goods went back to the vendor and left United stock.</p>}
            {status === 'approved' && (
              <p>
                Credited {money(application?.amount ?? creditedAmountOf(note))}: {money(application?.toBill ?? 0)} on the
                bill
                {application?.toCreditBalance ? `, ${money(application.toCreditBalance)} to the vendor's credit balance` : ''}
                .
                {application?.accountsPayableId && (
                  <>
                    {' '}
                    <Link
                      to={`/accounts/${application.accountsPayableId}`}
                      className="font-medium text-primary-600 hover:underline"
                    >
                      Open bill
                    </Link>
                  </>
                )}
              </p>
            )}
            {status === 'rejected' && (
              <>
                <p>The vendor refused this return. It no longer counts against the invoice.</p>
                {note.rejectionNote && <p>Note: {note.rejectionNote}</p>}
                {goodsState === 'held' && (
                  <p>
                    Its goods are still held in their batch: counted in stock, but no order can take them. Release them
                    back into free stock, or ask an Admin to write them off.
                  </p>
                )}
                {goodsState === 'write_off_requested' && (
                  <>
                    <p>
                      Write-off requested
                      {note.goods?.writeOffRequestedAt ? ` on ${formatDate(note.goods.writeOffRequestedAt, '')}` : ''}.{' '}
                      {isAdmin
                        ? 'Approving it removes the held goods from United stock.'
                        : 'Waiting for an Admin; the goods stay held until then.'}
                    </p>
                    {note.goods?.writeOffNote && <p>Write-off note: {note.goods.writeOffNote}</p>}
                  </>
                )}
                {goodsState === 'released' && <p>Its held goods were released back into free stock.</p>}
                {goodsState === 'written_off' && <p>An Admin wrote its goods off. They left United stock.</p>}
              </>
            )}
            {status === 'cancelled' && (
              <p>
                This return is cancelled. It no longer counts against the invoice.
                {goodsState === 'released' ? ' Its held goods were released back into free stock.' : ''}
              </p>
            )}
            {status === 'reversed' && (
              <>
                <p>
                  An Admin reversed this return. The bill went back up
                  {goodsState === 'restored' ? ', and the goods went back into their batch' : ''}.
                </p>
                {note.reversalNote && <p>Note: {note.reversalNote}</p>}
              </>
            )}
          </div>
        )}

        {/* Read-only past Draft, or for someone who may not write it. A disabled
            fieldset disables every control inside it, so no field can be missed;
            the server refuses the edit either way. */}
        <fieldset disabled={readOnly} className="m-0 min-w-0 space-y-4 border-0 p-0">
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
            <div className="text-xs font-semibold text-slate-700">Vendor invoice</div>
            {note || returnable ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-slate-700">
                  <span className="font-semibold">{invoiceNumber || '--'}</span>
                  {` · ${vendorName || '--'}`}
                  {invoiceDate ? ` · ${formatDate(invoiceDate, '')}` : ''}
                  {currency ? ` · ${currency}` : ''}
                </div>
                {note ? (
                  <span className="text-xs text-slate-400">A return stays on the invoice it was raised against.</span>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => pickInvoice('')}>
                    Choose a different invoice
                  </Button>
                )}
              </div>
            ) : (
              <VendorInvoicePicker onPick={pickInvoice} />
            )}
            {returnableLoading && <div className="text-xs text-slate-500">Loading the lines on this invoice…</div>}
            {returnableError && <ErrorState message={returnableError} />}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
            <div className="text-xs font-semibold text-slate-700">What is being returned</div>
            <SegmentedControl
              aria-label="What is being returned"
              value={kind}
              onChange={changeKind}
              segments={KIND_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
            />
            <p className="text-xs text-slate-500">
              <span className="font-medium text-slate-600">Money only.</span> Price mistake, rebate, or goods not
              returned: type an amount excluding VAT per line.
            </p>
          </div>

          {(returnable || note) && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-xs font-semibold text-slate-700">Lines</div>
                {currency && <div className="text-xs text-slate-400">Amounts in {currency}</div>}
              </div>
              {!returnable && !returnableLoading && returnableError && (
                <div className="mb-2 text-xs text-slate-500">
                  Showing this return&apos;s own lines: the invoice&apos;s lines could not be read.
                </div>
              )}
              {/* Wide by nature, so it scrolls inside its card on a phone rather than
                  pushing the page sideways. Dense: a grid read and typed in bulk. */}
              <div className="overflow-x-auto">
                <table className="data-table data-table-grid data-table-dense min-w-[1100px]">
                  <thead>
                    <tr>
                      <th className="w-16">Include</th>
                      <th className="w-10">#</th>
                      <th>Description</th>
                      <th className="text-right">Billed</th>
                      <th className="text-right">Unit price</th>
                      <th className="text-right">VAT %</th>
                      <th className="text-right">Left to return</th>
                      <th>Already on</th>
                      {kind === 'goods' && <th className="w-28">Quantity</th>}
                      <th className="w-32">Amount excl. VAT</th>
                      <th className="text-right">VAT</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLines.length === 0 && (
                      <tr>
                        <td colSpan={kind === 'goods' ? 12 : 11} className="py-6 text-center text-slate-400">
                          This invoice has no lines.
                        </td>
                      </tr>
                    )}
                    {tableLines.map((line) => {
                      const entry = entries[line.lineNumber] ?? UNTOUCHED;
                      const blocked = blockedReason(line, fromInvoice, goodsDraft);
                      const flagged = flaggedLines.includes(line.lineNumber);
                      const figures = entry.included ? figureByLine.get(line.lineNumber) : undefined;
                      const inputsOff = !entry.included || Boolean(blocked);
                      const amountValue =
                        entry.amount ?? (kind === 'goods' && figures ? figures.amount.toFixed(2) : '');
                      const batchFacts = goodsDraft && fromInvoice && line.batch ? batchFactsOf(line.batch, line.unit) : [];
                      // A goods draft on weighed boxes ticks boxes instead of typing a quantity.
                      const boxes = goodsDraft && fromInvoice ? line.batch?.boxes ?? null : null;
                      // Every cell is align-top so the boxes share a line when a row grows a hint.
                      return (
                        <tr
                          key={line.lineNumber}
                          data-flagged={flagged ? 'true' : undefined}
                          className={flagged ? 'bg-red-50' : blocked ? 'text-slate-400' : ''}
                        >
                          <td className="align-top">
                            <input
                              type="checkbox"
                              aria-label={`Include line ${line.lineNumber}`}
                              className="mt-1.5 h-4 w-4"
                              checked={entry.included}
                              // A ticked line that has since become blocked can still be unticked.
                              disabled={Boolean(blocked) && !entry.included}
                              onChange={(e) => toggleLine(line, e.target.checked)}
                            />
                          </td>
                          <td className="align-top tabular-nums text-slate-500">{line.lineNumber}</td>
                          <td className="align-top">
                            <div className={blocked ? '' : 'text-slate-800'}>{line.description || `Line ${line.lineNumber}`}</div>
                            {line.sku && <div className="text-slate-400">{line.sku}</div>}
                            {blocked && <div className="mt-0.5 text-amber-700">{blocked}</div>}
                          </td>
                          <td className="align-top text-right tabular-nums whitespace-nowrap">
                            {quantityWithUnit(line.quantity, line.unit)}
                          </td>
                          <td className="align-top text-right tabular-nums whitespace-nowrap">{money(line.unitPrice)}</td>
                          <td className="align-top text-right tabular-nums">
                            {line.taxRate === null ? '--' : `${line.taxRate}%`}
                            {/* Staff only: the vendor's copy shows the rate like any other (D44 #28). */}
                            {line.taxRateAssumed && (
                              <div
                                className="whitespace-normal text-amber-700"
                                data-testid={`vat-assumed-${line.lineNumber}`}
                              >
                                assumed, the invoice gives no rate
                              </div>
                            )}
                          </td>
                          <td className="align-top text-right tabular-nums whitespace-nowrap">
                            <div>{quantityWithUnit(line.remainingQuantity, line.unit)}</div>
                            <div className="text-slate-500">{money(line.remainingAmount)}</div>
                            {/* What the batch can give (VR-3): the billed cap above is not
                                the whole story once orders reserve or other returns hold. */}
                            {batchFacts.length > 0 && (
                              <div
                                className="mt-0.5 whitespace-normal text-slate-500"
                                data-testid={`batch-facts-${line.lineNumber}`}
                              >
                                {batchFacts.map((fact) => (
                                  <div key={fact}>{fact}</div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="align-top">{line.returnedOn.length ? line.returnedOn.join(', ') : '--'}</td>
                          {kind === 'goods' && (
                            <td className="align-top">
                              {boxes ? (
                                <BoxPicker
                                  lineNumber={line.lineNumber}
                                  unit={line.unit}
                                  boxes={boxes}
                                  selected={entry.caseSeqs ?? []}
                                  disabled={inputsOff}
                                  invalid={flagged}
                                  quantity={figures?.quantity ?? null}
                                  onToggle={(seq, ticked) => toggleBox(line.lineNumber, seq, ticked)}
                                />
                              ) : (
                                <>
                                  <input
                                    type="number"
                                    min="0"
                                    step={line.wholeUnitsOnly ? 1 : 0.001}
                                    max={line.remainingQuantity ?? undefined}
                                    aria-label={`Quantity for line ${line.lineNumber}`}
                                    aria-invalid={flagged || undefined}
                                    className="w-full h-8 rounded-md border border-slate-200 px-2 text-xs text-right"
                                    value={entry.quantity}
                                    // Boxes are the quantity: never typed over (only reachable
                                    // when the invoice's lines could not be read).
                                    disabled={inputsOff || entry.caseSeqs !== null}
                                    placeholder={line.remainingQuantity !== null ? `max ${line.remainingQuantity}` : ''}
                                    onChange={(e) => setQuantity(line, e.target.value)}
                                  />
                                  {entry.caseSeqs && entry.caseSeqs.length > 0 && (
                                    <div className="mt-0.5 text-slate-500" data-testid={`boxes-${line.lineNumber}`}>
                                      {entry.caseSeqs.length === 1 ? 'Box' : 'Boxes'} {entry.caseSeqs.join(', ')}
                                    </div>
                                  )}
                                  {line.wholeUnitsOnly && line.unit && !entry.caseSeqs && (
                                    <div className="mt-0.5 text-slate-400">whole {line.unit}</div>
                                  )}
                                </>
                              )}
                            </td>
                          )}
                          <td className="align-top">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              aria-label={`Amount excluding VAT for line ${line.lineNumber}`}
                              aria-invalid={flagged || undefined}
                              className="w-full h-8 rounded-md border border-slate-200 px-2 text-xs text-right"
                              value={amountValue}
                              disabled={inputsOff}
                              placeholder={
                                kind === 'goods'
                                  ? 'Suggested'
                                  : line.remainingAmount !== null
                                  ? `max ${line.remainingAmount.toFixed(2)}`
                                  : ''
                              }
                              onChange={(e) => setAmount(line.lineNumber, e.target.value)}
                              onBlur={kind === 'goods' ? () => releaseAmount(line.lineNumber) : undefined}
                            />
                          </td>
                          <td className="align-top text-right tabular-nums whitespace-nowrap">
                            {figures ? money(figures.vatAmount) : '--'}
                          </td>
                          <td className="align-top text-right font-semibold tabular-nums whitespace-nowrap">
                            {figures ? money(figures.total) : '--'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {editable && fromInvoice && draftLines.length === 0 && (
                <p className="mt-2 text-xs text-slate-500">Tick the lines this return is for.</p>
              )}
              {editable && draftLines.length > 0 && incomplete && (
                <p className="mt-2 text-xs text-slate-500">
                  {kind === 'goods'
                    ? 'Type how many go back, or tick the boxes, on every ticked line to see the totals.'
                    : 'Type an amount excluding VAT on every ticked line to see the totals.'}
                </p>
              )}
              {previewError && (
                <div className="mt-3">
                  <ErrorState message={previewError} />
                </div>
              )}

              <dl className="mt-3 ml-auto w-full max-w-xs space-y-1 text-xs">
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Subtotal</dt>
                  <dd className="tabular-nums text-slate-800">{money(preview?.subtotal)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">VAT</dt>
                  <dd className="tabular-nums text-slate-800">{money(preview?.vatTotal)}</dd>
                </div>
                <div className="flex justify-between gap-4 border-t border-slate-200 pt-1 text-sm font-semibold">
                  <dt className="text-slate-700">Total</dt>
                  <dd className="tabular-nums text-slate-900" data-testid="debit-note-total">
                    {money(preview?.total)}
                  </dd>
                </div>
                {editable && previewing && <div className="text-right text-slate-400">Working out the totals…</div>}
              </dl>
            </div>
          )}

          {/* D44: why the return is raised. One reason for the whole return. */}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-3 text-xs font-semibold text-slate-700">Reason for the return</div>
            <CreditReasonField
              reasons={reasons}
              reasonKey={reasonKey}
              reasonNote={reasonNote}
              savedKey={note?.reasonKey}
              onChange={(next) => {
                setReasonKey(next.reasonKey);
                setReasonNote(next.reasonNote);
              }}
              hint="Printed on the vendor's copy (the PDF and the email)."
            />
          </div>
        </fieldset>

        {/* Outside the fieldset: the vendor's credit note arrives after the return is sent. */}
        {note && <VendorCreditNotePanel note={note} isAdmin={isAdmin} canEdit={canEdit} onUpdated={applyNote} />}

        {note?.history && note.history.length > 0 && <DebitNoteHistory history={note.history} />}
      </div>

      {emailDialog && note && (
        <EmailToVendorDialog
          debitNoteNumber={note.debitNoteNumber}
          initialTo={emailDialog.to}
          vendorMatched={emailDialog.vendorMatched}
          attachmentName={emailDialog.attachmentName}
          goods={note.kind === 'goods'}
          busy={busy === 'email'}
          onCancel={() => setEmailDialog(null)}
          onSend={handleEmailSend}
        />
      )}

      {dialog && note && (
        <NoteDialog
          {...dialogCopy}
          busy={busyNow}
          onCancel={() => setDialog(null)}
          onConfirm={(text) => runNoteAction(dialog, text)}
        />
      )}
    </div>
  );
};

export default DebitNoteDetailPage;
